import { zodResolver } from '@hookform/resolvers/zod'
import { UsersIcon } from '@phosphor-icons/react'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { getClientsWithCount } from '@/api/clients/get-clients-with-count'
import { useAddVisitorGuest, useAddVisitorVendor, useUpdateVisitorGuest } from '@/api/visitors'
import { getGuestDetails, getGuestDetailsQueryKey } from '@/api/visitors/get-guest-details'
import { NumericInputFormField } from '@/components/form/numeric-input-form-field'
import { SelectFormField } from '@/components/form/select-form-field'
import { TextAreaFormField } from '@/components/form/text-area-form-field'
import { TextInputFormField } from '@/components/form/text-input-form-field'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { FileUpload } from '@/components/ui/file-upload'
import { BorderLoader } from '@/components/ui/loaders/border-loader'
import { Modal } from '@/components/ui/modal'
import { NavbarMenu, NavbarMenuItem } from '@/components/ui/navbar-menu'
import { Select, useSelectOptions } from '@/components/ui/select'
import { DEFAULT_TZ } from '@/constants'
import { toastFactory } from '@/factories'
import { useFormSave } from '@/hooks'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { getApiErrorMessage } from '@/utils/error'

import { AccessBlock } from './access-block'
import {
  COI_ACCEPTED_TYPES,
  getAccessTypeOptions,
  getGuestCategoryOptions,
  getVisitorTypeOptions,
  MAX_COI_SIZE,
} from './add-visitor-modal.constants'
import * as S from './add-visitor-modal.styled'
import {
  AccessType,
  AccessTypeEnum,
  AddVisitorFormValues,
  AddVisitorModalProps,
  MAX_NOTE_LENGTH,
  makeAddVisitorSchema,
  PermissionType,
  PermissionTypeEnum,
  VisitorType,
  VisitorTypeEnum,
} from './add-visitor-modal.types'
import {
  formatGuestDetailsToFormValues,
  formatGuestFormData,
  formatValues,
  formatVendorFormData,
  getDefaultValues,
  getOneTimeVisitWindow,
  parseMonthlyPattern,
} from './add-visitor-modal.utils'
import { PermissionSection } from './permission-section'

export const AddVisitorModal = ({
  onClose,
  editingVisitor,
  defaultVisitorType,
  lockResident,
  lockBuilding,
}: AddVisitorModalProps) => {
  const { t } = useTranslation(['visitors', 'common'])
  const globalBuilding = useSelector(selectActiveBuilding)
  const building = lockBuilding ?? globalBuilding
  const timezone = building?.timezone || 'America/Chicago'
  const VISITOR_TYPE_OPTIONS = useMemo(() => getVisitorTypeOptions(t), [t])
  const ACCESS_TYPE_OPTIONS = useMemo(() => getAccessTypeOptions(t), [t])
  const GUEST_CATEGORY_SELECT_OPTIONS = useMemo(() => getGuestCategoryOptions(t), [t])
  const addVisitorSchema = useMemo(() => makeAddVisitorSchema(t), [t])

  const { mutateAsync: addGuest, isPending: isAddingGuest } = useAddVisitorGuest()
  const { mutateAsync: updateGuest, isPending: isUpdatingGuest } = useUpdateVisitorGuest()
  const { mutateAsync: addVendor, isPending: isAddingVendor } = useAddVisitorVendor()

  const isPending = isAddingGuest || isUpdatingGuest || isAddingVendor

  const isUpdate = !!editingVisitor?.id

  const { data: guestDetails, isLoading: isPrefillLoading } = useQuery({
    enabled: isUpdate,
    gcTime: 0,
    queryFn: async () => {
      try {
        return await getGuestDetails(editingVisitor!.id)
      } catch (error) {
        toastFactory.error(getApiErrorMessage(error))
        onClose()
        throw error
      }
    },
    queryKey: getGuestDetailsQueryKey(editingVisitor?.id ?? 0),
  })

  const editFormValues =
    isUpdate && guestDetails
      ? (() => {
          const mapped = formatGuestDetailsToFormValues(guestDetails, building?.timezone)
          const permissionTypeId = (mapped.permissionType?.id ??
            PermissionTypeEnum.enum.custom) as PermissionType
          const isRecurringEdit = permissionTypeId !== PermissionTypeEnum.enum.custom
          const resident = String(editingVisitor!.clientId)
          return {
            accessType: isRecurringEdit
              ? AccessTypeEnum.enum.recurring
              : AccessTypeEnum.enum['one-time'],
            coi: null,
            company: mapped.visitorCompany ?? '',
            customAccess: mapped.customAccess ?? false,
            dueDate: mapped.dueDate ?? null,
            email: mapped.visitorEmail ?? '',
            endOfDay: mapped.endOfDay ?? null,
            guestCategory: mapped.visitorType?.id ?? '',
            isNoteOpen: mapped.isVisitorNoteOpen ?? false,
            // Note: As it was described in EL-14633 - keylessAccess is temporarly treated as physical key access,
            // and keyAccess is treated as keyless integration
            keyAccess: mapped.keyAccess ?? false,
            keylessAccess: mapped.keylessAccess ?? false,
            monthlyPermission: mapped.monthlyPermission ?? '',
            monthlyRange: parseMonthlyPattern(
              mapped.monthlyPermission ?? '',
              mapped.dueDate ?? null,
            ),
            name: mapped.visitorName ?? '',
            note: mapped.visitorNote ?? '',
            permissionType: permissionTypeId,
            phone: mapped.visitorPhone ?? '',
            pinCode: mapped.pinCode ?? false,
            resident,
            selectedDays: mapped.selectedDays ?? [],
            startOfDay: mapped.startOfDay ?? null,
            timeEnd: mapped.timeEnd ?? null,
            timeStart: mapped.timeStart ?? null,
            visitorId: mapped.visitorId ?? '',
            visitorType: VisitorTypeEnum.enum.guest,
          }
        })()
      : undefined

  const form = useForm<AddVisitorFormValues>({
    defaultValues: getDefaultValues(
      building?.timezone ?? undefined,
      defaultVisitorType,
      lockResident ? String(lockResident.id) : undefined,
    ),
    resolver: zodResolver(addVisitorSchema),
    values: editFormValues,
  })

  const {
    setValue,
    formState: { errors },
  } = form

  const visitorType = useWatch({ control: form.control, name: 'visitorType' })
  const accessType = useWatch({ control: form.control, name: 'accessType' })
  const isNoteOpen = useWatch({ control: form.control, name: 'isNoteOpen' })
  const coi = useWatch({ control: form.control, name: 'coi' })
  const keyAccess = useWatch({ control: form.control, name: 'keyAccess' })
  const pinCode = useWatch({ control: form.control, name: 'pinCode' })

  const isGuest = visitorType === VisitorTypeEnum.enum.guest
  const isVendor = visitorType === VisitorTypeEnum.enum.vendor
  const isRecurring = accessType === AccessTypeEnum.enum.recurring
  // Note: Keyless Access and PIN code access require an explicit, bounded visit window, so the
  // permission section (with its date/time fields) must show even for a one-time visitor.
  const isKeyIntegrationEnabled = !!keyAccess || !!pinCode

  const visitorTypeLabel = isGuest
    ? t('visitors:modalTypes.guest')
    : t('visitors:modalTypes.vendor')
  const modalTitle = t(isUpdate ? 'visitors:form.titleEdit' : 'visitors:form.titleAdd', {
    type: visitorTypeLabel,
  })

  const residentOptions = useSelectOptions(
    {
      query: getClientsWithCount,
      requestData: { buildingId: building?.id },
      transform: (data) => {
        const options = (data?.clients ?? []).map((client) => ({
          label: `${client.user.firstName} ${client.user.lastName} (#${client.apartment})`,
          value: String(client.id),
        }))

        if (lockResident && !options.some((opt) => opt.value === String(lockResident.id))) {
          options.unshift({ label: lockResident.name, value: String(lockResident.id) })
        }

        return options
      },
    },
    {
      enabled: !!building?.id,
      queryKey: ['getClientsWithCount', building?.id],
    },
  )

  // Note: Reset access-type-specific fields when access type changes
  useEffect(() => {
    if (!isRecurring) {
      setValue('permissionType', PermissionTypeEnum.enum.custom)
      setValue('selectedDays', [])
      setValue('monthlyPermission', '')
      setValue('monthlyRange', [null, null])
    }
  }, [isRecurring, setValue])

  // Note: The access toggles (physical key, concierge/custom access, keyless, PIN) are only
  // meaningful for the visitor type / access type combination they were set under - e.g.
  // keyAccess and pinCode are hidden for vendors and keyAccess is only shown while recurring, so
  // switching visitor type or access type must reset them or a hidden toggle can silently keep
  // forcing the custom permission type and its validation.
  const resetAccessToggles = () => {
    setValue('keylessAccess', false, { shouldDirty: true })
    setValue('customAccess', false, { shouldDirty: true })
    setValue('keyAccess', false, { shouldDirty: true })
    setValue('pinCode', false, { shouldDirty: true })
  }

  const handleVisitorTypeChange = (value: VisitorType) => {
    setValue('visitorType', value, { shouldDirty: true, shouldValidate: true })
    setValue('resident', null, { shouldDirty: true })
    setValue('guestCategory', '', { shouldDirty: true })
    setValue('coi', null, { shouldDirty: true })
    resetAccessToggles()
  }

  const handleAccessTypeChange = (value: AccessType) => {
    setValue('accessType', value, { shouldDirty: true, shouldValidate: true })
    resetAccessToggles()
  }

  const handleResidentChange = (value: string | null | undefined) => {
    setValue('resident', value ?? null, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    })
    setValue('keyAccess', false, { shouldDirty: true })
    setValue('pinCode', false, { shouldDirty: true })
  }

  const onSubmit = async (values: AddVisitorFormValues) => {
    if (!building) return

    const isRecurringSubmit = values.accessType === AccessTypeEnum.enum.recurring
    const isKeyIntegrationSubmit = !!values.keyAccess || !!values.pinCode
    const timezone = building.timezone ?? 'America/Chicago'

    // Note: A new one-time visitor without a key integration is valid from now, so its window
    // is computed on submit and any date/time left over from the recurring form is discarded.
    // Editing, recurring visitors, and key-integration (keyless/PIN) visitors all keep the
    // window entered in the form instead, since key integrations require an explicit window.
    const formatted = formatValues(
      isRecurringSubmit || isUpdate || isKeyIntegrationSubmit
        ? values
        : { ...values, ...getOneTimeVisitWindow(timezone) },
    )

    try {
      if (isGuest) {
        const { clientId, formData } = formatGuestFormData(formatted, isRecurringSubmit, timezone)
        if (isUpdate) {
          await updateGuest({ formData, id: editingVisitor!.id })
          toastFactory.success(t('visitors:snackbar.visitorUpdated'))
          onClose()
          return
        }

        await addGuest({ clientId, formData })
        toastFactory.success(t('visitors:dashboard.snackbar.visitorAdded', { name: values.name }))
        onClose()
        return
      }

      const formData = formatVendorFormData(formatted, isRecurringSubmit, building.id, timezone)
      await addVendor({ formData })
      onClose()
      toastFactory.success(t('visitors:dashboard.snackbar.visitorAdded', { name: values.name }))
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleSave = useFormSave({ form, isEdit: isUpdate, onSubmit, onUnchanged: onClose })

  return (
    <Modal
      title={modalTitle}
      headerIcon={UsersIcon}
      height="74.8rem"
      footer={
        <>
          <Button use="secondary" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button loading={isPending} onClick={handleSave}>
            {isUpdate ? t('visitors:dashboard.addVisitorModal.update') : t('common:actions.save')}
          </Button>
        </>
      }
      onClose={onClose}
    >
      {isPrefillLoading ? (
        <BorderLoader align="absolute-center" />
      ) : (
        <FormProvider {...form}>
          <S.Content onSubmit={handleSave}>
            {/* Visitor type selector */}
            <S.SelectorSection>
              <S.SelectorLabel>
                {t('visitors:dashboard.addVisitorModal.visitorTypeLabel')}
              </S.SelectorLabel>
              <NavbarMenu disableChildrenWrap mode="tabs">
                {VISITOR_TYPE_OPTIONS.filter(
                  (opt) => !lockResident || opt.value === visitorType,
                ).map((opt) => (
                  <NavbarMenuItem
                    key={opt.value}
                    mode="tabs"
                    icon={opt.icon}
                    active={visitorType === opt.value}
                    onClick={() => handleVisitorTypeChange(opt.value)}
                  >
                    {opt.label}
                  </NavbarMenuItem>
                ))}
              </NavbarMenu>
            </S.SelectorSection>

            {/* Access type selector */}
            <S.SelectorSection>
              <S.SelectorLabel>{t('visitors:dashboard.columns.accessType')}</S.SelectorLabel>
              <NavbarMenu disableChildrenWrap mode="tabs">
                {ACCESS_TYPE_OPTIONS.map((opt) => (
                  <NavbarMenuItem
                    key={opt.value}
                    mode="tabs"
                    icon={opt.icon}
                    active={accessType === opt.value}
                    onClick={() => handleAccessTypeChange(opt.value)}
                  >
                    {opt.label}
                  </NavbarMenuItem>
                ))}
              </NavbarMenu>
            </S.SelectorSection>

            <S.FormSection>
              {/* Resident select (required for guests, optional for vendors) */}
              <Select
                searchable
                label={
                  isGuest
                    ? t('visitors:dashboard.addVisitorModal.residentLabelRequired')
                    : t('visitors:dashboard.addVisitorModal.residentLabel')
                }
                tooltip={t('visitors:dashboard.addVisitorModal.residentTooltip')}
                placeholder={t('visitors:dashboard.addVisitorModal.selectResidentPlaceholder')}
                value={form.watch('resident') ?? null}
                error={errors.resident?.message}
                {...residentOptions.selectProps}
                disabled={!!lockResident}
                onChange={handleResidentChange}
              />

              {/* Name + Guest type (same row for one-time guests) */}
              {isGuest && !isRecurring ? (
                <S.FieldGrid>
                  <TextInputFormField
                    name="name"
                    label={t('visitors:dashboard.addVisitorModal.guestNameLabel')}
                    placeholder={t('visitors:form.enterFieldValue', {
                      field: t('visitors:form.fieldName').toLowerCase(),
                      type: t('visitors:modalTypes.guest').toLowerCase(),
                    })}
                  />
                  <SelectFormField<AddVisitorFormValues>
                    name="guestCategory"
                    label={t('visitors:dashboard.addVisitorModal.guestTypeLabel')}
                    placeholder={t('visitors:dashboard.addVisitorModal.selectTypePlaceholder')}
                    options={GUEST_CATEGORY_SELECT_OPTIONS}
                  />
                </S.FieldGrid>
              ) : (
                <TextInputFormField
                  name="name"
                  label={
                    isGuest
                      ? t('visitors:dashboard.addVisitorModal.guestNameLabel')
                      : t('visitors:dashboard.addVisitorModal.vendorNameLabel')
                  }
                  placeholder={t('visitors:form.enterFieldValue', {
                    field: t('visitors:form.fieldName').toLowerCase(),
                    type: visitorTypeLabel.toLowerCase(),
                  })}
                />
              )}

              {/* Phone + Email */}
              <S.FieldGrid>
                <NumericInputFormField
                  name="phone"
                  label={
                    isGuest
                      ? t('visitors:dashboard.addVisitorModal.guestPhoneLabel')
                      : t('visitors:dashboard.addVisitorModal.vendorPhoneLabel')
                  }
                  placeholder={t('visitors:dashboard.addVisitorModal.enterPhonePlaceholder')}
                />
                <TextInputFormField
                  name="email"
                  label={
                    isGuest
                      ? t('visitors:dashboard.addVisitorModal.guestEmailLabel')
                      : t('visitors:dashboard.addVisitorModal.vendorEmailLabel')
                  }
                  placeholder={t('visitors:dashboard.addVisitorModal.enterEmailPlaceholder')}
                  type="email"
                />
              </S.FieldGrid>

              {/* Company + ID */}
              <S.FieldGrid>
                <TextInputFormField
                  name="company"
                  label={t('visitors:form.fieldCompany')}
                  placeholder={t('visitors:dashboard.addVisitorModal.enterCompanyPlaceholder')}
                />
                <TextInputFormField
                  name="visitorId"
                  label={
                    isGuest
                      ? t('visitors:detailsModal.guestId')
                      : t('visitors:detailsModal.vendorId')
                  }
                  placeholder={t('visitors:dashboard.addVisitorModal.enterIdPlaceholder')}
                />
              </S.FieldGrid>
            </S.FormSection>

            {/* COI upload (vendors only) */}
            {isVendor && (
              <FileUpload
                label={t('visitors:form.uploadCoi')}
                description={t('visitors:form.coiFileInfo', { formats: 'PDF', maxSize: 10 })}
                files={coi ? [coi] : []}
                multiple={false}
                maxFiles={1}
                maxFileSize={MAX_COI_SIZE}
                acceptedTypes={COI_ACCEPTED_TYPES}
                layout="minimized"
                onChange={(files) => setValue('coi', files[0] ?? null, { shouldDirty: true })}
              />
            )}

            {/* Note toggle */}
            <Checkbox
              label={t('visitors:form.addNote')}
              checked={isNoteOpen}
              onChange={(e) =>
                setValue('isNoteOpen', e.target.checked, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
            />
            {isNoteOpen && (
              <TextAreaFormField
                name="note"
                label={t('visitors:detailsModal.note')}
                maxLength={MAX_NOTE_LENGTH}
                placeholder={t('visitors:dashboard.addVisitorModal.notePlaceholder')}
              />
            )}

            {(isRecurring || isKeyIntegrationEnabled) && (
              <>
                <S.Divider />
                <PermissionSection />
              </>
            )}

            <S.Divider />

            {/* Access toggles (physical key, PIN, etc.) */}
            <AccessBlock isVendor={isVendor} isRecurring={isRecurring} />
          </S.Content>
        </FormProvider>
      )}
    </Modal>
  )
}
